"use client"

import { useTheme } from "next-themes"
import { Toaster as Sonner, type ToasterProps } from "sonner"
import { CircleCheckIcon, InfoIcon, TriangleAlertIcon, OctagonXIcon, Loader2Icon } from "lucide-react"

/**
 * O ALTO DA TELA NO IPHONE INSTALADO (iOS 26+).
 *
 * O balão abre em cima e caía DENTRO da rampa do iOS: a faixa que o sistema
 * borra por cima da webview, da barra de status até ~40pt abaixo dela (o
 * porquê inteiro está no `app/globals.css`, na folga do `pt-safe`). O
 * cabeçalho do celular já sai dela por aquele `pt-safe`, mas o balão é
 * `fixed` na viewport e não passa por cabeçalho nenhum — no celular do
 * mestre o erro da solicitação de compra abria borrado, ilegível
 * ("não consigo ver o erro no balão que ele abre lá em cima me dizendo
 * porque não tá aceitando", 06/10/2026).
 *
 * A folga é a mesma do `pt-safe` (área segura + rampa) somada ao respiro
 * que o sonner já usava: 24px no computador e 16px no celular, os padrões
 * dele. Fora do iPhone instalado e de pé os dois valem zero e nada muda.
 * O sonner escreve estes valores direto em `--offset-top` e
 * `--mobile-offset-top`, de onde o CSS dele tira o `top` — então o `calc()`
 * resolve no aparelho.
 *
 * O corte do celular no sonner é 600px, menor que o `md` do Tailwind: por
 * isso os DOIS ganham a folga, senão o iPad instalado e de pé (que tem
 * rampa, mas usa o offset do computador) ficaria de fora.
 */
const RAMPA = "env(safe-area-inset-top) + var(--ios-top-ramp, 0px)"
const TOP_OFFSET = `calc(${RAMPA} + 24px)`
const TOP_OFFSET_MOBILE = `calc(${RAMPA} + 16px)`

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      icons={{
        success: (
          <CircleCheckIcon className="size-4" />
        ),
        info: (
          <InfoIcon className="size-4" />
        ),
        warning: (
          <TriangleAlertIcon className="size-4" />
        ),
        error: (
          <OctagonXIcon className="size-4" />
        ),
        loading: (
          <Loader2Icon className="size-4 animate-spin" />
        ),
      }}
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
          "--border-radius": "var(--radius)",
        } as React.CSSProperties
      }
      toastOptions={{
        classNames: {
          toast: "cn-toast",
        },
      }}
      offset={{ top: TOP_OFFSET }}
      mobileOffset={{ top: TOP_OFFSET_MOBILE }}
      {...props}
    />
  )
}

export { Toaster }
